// ccp/src/lib/coachingTriggers.js
//
// Copilot coaching v1 — the two named triggers and the per-call debounce, as
// PURE functions (no React, no fetch) so scripts/verify-coaching-triggers.mjs
// can prove them without a browser.
//
// Scope: docs/ops/copilot-coaching-scoping-2026-09-14.md (with the same-day
// correction). Two triggers, both reusing detection that already exists;
// nothing here classifies anything itself:
//
//   1. escalation-keyword — the D29 escalation context on the contact
//      (display.escalation.trigger === 'keyword' | 'specialist-offer'). This
//      is a one-shot signal set at handover, so the trigger fires at most
//      once per contact, when the agent connects.
//
//   2. sustained-negative-sentiment — 2+ consecutive NEGATIVE labels on the
//      CUSTOMER's lines of the live transcript feed, not a single blip. Fires
//      once per negative run; re-arms only after a non-NEGATIVE customer line.
//      The feed is the Phase 3 Contact Lens poller (TranscriptContext lines,
//      each with `speaker: 'Cust' | 'You'` and a per-utterance `sentiment`
//      label). ⚠️ D35 (2026-09-16): on a live call `display.transcript` is
//      ALWAYS empty (connectData.js sets it to [] and nothing fills it); the
//      feed lives in TranscriptContext. The deployed v1 evaluated `display`
//      alone, so this trigger never fired on the first real call even though
//      the customer ran three consecutive NEGATIVE labels. The hook must pass
//      the feed through coachingView() below — see the D35 checks in
//      scripts/verify-coaching-triggers.mjs.
//
// Debounce: hard floor, max ONE suggestion per 30 s per contact, whichever
// trigger. No ranking — if both qualify on the same evaluation, the first in
// TRIGGERS order wins and the other waits for its next evaluation.
//
// `kind` is carried on each trigger so the future "verbatim confirmation"
// trigger (a required action, not a risk signal — named in the scoping doc,
// not built) can be added as kind: 'required-action' without reshaping these.

export const DEBOUNCE_MS = 30_000;
export const NEGATIVE_RUN_MIN = 2;

import { isTransferMethod } from './contactIds.js';
const ESCALATION_TRIGGERS = new Set(['keyword', 'specialist-offer']);

// Phase 3 feed adapter. LiveTranscript renders lines as
// { speaker: 'AI' | 'Cust' | 'You', text, time, entities }; the poller is
// expected to add `sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE'` per line
// (the Contact Lens Transcript.Sentiment label, decided 2026-09-10). Lines
// with no label are ignored, never treated as neutral.
export function customerSentimentLabels(display) {
  const lines = Array.isArray(display?.transcript) ? display.transcript : [];
  return lines
    .filter(l => l && l.speaker === 'Cust' && typeof l.sentiment === 'string')
    .map(l => l.sentiment.toUpperCase());
}

// D35: the ONE place the triggers' transcript comes from. `feedLines` is the
// Phase 3 feed (useTranscript().lines — live poller, or the mock seed in demo
// mode). display.transcript is deliberately overwritten, never read: on live
// calls it is an empty placeholder and evaluating it means trigger 2 is dead.
export function coachingView(display, feedLines) {
  return { ...(display ?? {}), transcript: Array.isArray(feedLines) ? feedLines : [] };
}

// Length of the trailing run of NEGATIVE customer labels, and an identity for
// that run (index where it started) so it fires once, not on every new line.
export function trailingNegativeRun(labels) {
  let n = 0;
  for (let i = labels.length - 1; i >= 0; i--) {
    if (labels[i] === 'NEGATIVE') n++; else break;
  }
  return { length: n, startIndex: n ? labels.length - n : -1 };
}

// Storm 2026-10-23: PII folded into coaching. Contact Lens replaces personal
// details in the live transcript with "[PII]"; the first caller line carrying
// it fires ONE fixed cue per contact. The cue is fixed text (no model call):
// the advice never changes and nothing about the caller's details is sent
// anywhere.
export const PII_MARKER = /\[PII\]/;
export const PII_CUE = 'The caller has shared personal details (hidden in the transcript). Don\u2019t read them back in full: confirm only the last few digits or letters.';

export const TRIGGERS = [
  {
    id: 'escalation-keyword',
    kind: 'risk-signal',
    label: 'Caller asked for a person',
    // Returns a context object when the trigger qualifies, else null.
    evaluate(display, state) {
      const esc = display?.escalation ?? {};
      if (!ESCALATION_TRIGGERS.has(esc.trigger)) return null;
      if (state.firedOnce.has('escalation-keyword')) return null;
      // Row 48 (2026-10-01): a transferred leg inherits the first leg's
      // escalation attributes, so the cue fired again for the agent who took
      // the transfer. The caller asked for a person once, on the first leg.
      if (isTransferMethod(display?.meta?.initiationMethod)) return null;
      return {
        key: 'escalation-keyword',           // one-shot: fires once per contact
        context: {
          escalation: {
            trigger: esc.trigger,
            reason: esc.reason ?? null,
            handoverQuestion: esc.handoverQuestion ?? null,
            lastQuestion: esc.lastQuestion ?? null,
            lastAnswer: esc.lastAnswer ?? null,
          },
          recentLines: recentLines(display),
        },
      };
    },
  },
  {
    id: 'personal-details',
    kind: 'risk-signal',
    label: 'Personal details shared',
    staticCue: PII_CUE,
    evaluate(display, state) {
      if (state.firedOnce.has('personal-details')) return null;
      const lines = Array.isArray(display?.transcript) ? display.transcript : [];
      const hit = lines.some(l => l && l.speaker === 'Cust' && typeof l.text === 'string' && PII_MARKER.test(l.text));
      if (!hit) return null;
      return { key: 'personal-details', context: {} };   // one-shot per contact
    },
  },
  {
    id: 'sustained-negative-sentiment',
    kind: 'risk-signal',
    label: 'Caller sentiment falling',
    evaluate(display, state) {
      const labels = customerSentimentLabels(display);
      const run = trailingNegativeRun(labels);
      if (run.length < NEGATIVE_RUN_MIN) return null;
      const key = `sustained-negative-sentiment:${run.startIndex}`;
      if (state.firedKeys.has(key)) return null;     // once per negative run
      return {
        key,
        context: {
          escalation: display?.escalation ?? null,
          recentLines: recentLines(display),
        },
      };
    },
  },
];

function recentLines(display, n = 8) {
  const lines = Array.isArray(display?.transcript) ? display.transcript : [];
  return lines
    .filter(l => l && !l.typing && typeof l.text === 'string')
    .slice(-n)
    .map(l => ({ speaker: l.speaker, text: l.text, sentiment: l.sentiment ?? null }));
}

// Per-contact debounce/one-shot state. Kept outside React so a remount
// mid-call cannot re-fire a trigger.
export function newCoachingState() {
  return { lastFiredAt: 0, firedOnce: new Set(), firedKeys: new Set() };
}

// Decide what (if anything) to fire right now. Pure: mutates nothing.
// Returns { trigger, key, context } | { waitMs } | null.
export function evaluateTriggers(display, state, now = Date.now()) {
  for (const trigger of TRIGGERS) {
    const hit = trigger.evaluate(display, state);
    if (!hit) continue;
    const since = now - state.lastFiredAt;
    if (state.lastFiredAt && since < DEBOUNCE_MS) {
      // Qualifies, but inside the 30 s floor: tell the caller when to re-check.
      return { waitMs: DEBOUNCE_MS - since };
    }
    return { trigger, key: hit.key, context: hit.context };
  }
  return null;
}

// Record a fire. Called only after the decision to call the API is made, so
// a failed API call still counts against the floor (the floor is a cost cap,
// not a delivery guarantee).
export function markFired(state, trigger, key, now = Date.now()) {
  state.lastFiredAt = now;
  state.firedKeys.add(key);
  if (key === trigger.id) state.firedOnce.add(trigger.id);
}
